/** Quick screen API client (`/api/screen` → auth :8100 `/screen`). */

async function req(path, { method = 'GET', body } = {}) {
  const res = await fetch(`/api/screen${path}`, {
    method,
    credentials: 'include',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      detail = j.detail || JSON.stringify(j);
    } catch {
      /* ignore */
    }
    const err = new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const enc = encodeURIComponent;

export const screenApi = {
  criteria: () => req('/criteria'),
  setCriteria: (body) => req('/criteria', { method: 'PUT', body }),
  deals: () => req('/deals'),
  deal: (dealId) => req(`/deals/${enc(dealId)}`),
  run: (dealId) => req(`/deals/${enc(dealId)}/run`, { method: 'POST' }),
};

export const OUTCOME_CLASS = {
  pass: 'qs-pass',
  review: 'qs-review',
  outside: 'qs-outside',
  nodata: 'qs-nodata',
};

export const STATUS_LABEL = {
  pass: 'Meets',
  review: 'Review',
  outside: 'Outside',
  nodata: 'No data',
};

/** Format a criterion value / threshold in its unit. */
export function fmtUnit(v, unit) {
  if (v == null || !Number.isFinite(Number(v))) return '—';
  const n = Number(v);
  switch (unit) {
    case '%':
      return `${(n * 100).toFixed(1)}%`;
    case '$MM':
      return `${n < 0 ? '−' : ''}$${Math.abs(n).toFixed(2)}MM`;
    case 'yrs':
      return `${n.toFixed(1)} yrs`;
    case 'x':
      return `${n.toFixed(2)}x`;
    case 'count':
      return String(Math.round(n));
    default:
      return String(n);
  }
}

export function thresholdText(c) {
  const op = c.direction === 'min' ? '≥' : '≤';
  return `${op} ${fmtUnit(c.pass, c.unit)} meets · ${op} ${fmtUnit(c.review, c.unit)} review`;
}

/** Poll a deal's screen until its background run finishes (or timeout). */
export async function waitForScreen(dealId, { start = false, intervalMs = 2500, timeoutMs = 600000, onTick } = {}) {
  let v = start ? await screenApi.run(dealId) : await screenApi.deal(dealId);
  const t0 = Date.now();
  while (v.running && Date.now() - t0 < timeoutMs) {
    onTick?.(v);
    await new Promise((r) => setTimeout(r, intervalMs));
    v = await screenApi.deal(dealId);
  }
  if (v.running) throw new Error('Screen still running after timeout — try again shortly.');
  if (v.job?.state === 'error') throw new Error(`Screen run failed: ${v.job.error}`);
  return v;
}

/** Compact assistant context for one screened deal. */
export function screenAssistantContext(view) {
  const r = view?.result;
  if (!r) return null;
  return {
    dealId: r.dealId,
    outcome: r.outcomeLabel,
    criteria: (r.criteria || []).map((c) => ({
      criterion: c.label,
      value: fmtUnit(c.value, c.unit),
      status: STATUS_LABEL[c.status],
      threshold: thresholdText(c),
      detail: c.detail || undefined,
    })),
    valueBridge: (r.valueBridge || []).map((b) => ({ line: b.label, valueMM: b.valueMM })),
    risks: (r.risks || []).map((k) => ({ severity: k.severity, title: k.title, detail: k.detail })),
    runAt: r.runAt,
  };
}

/** Dashboard outcome order: Pass → Review → Outside → Insufficient data → not run. */
export const OUTCOME_RANK = { pass: 0, review: 1, outside: 2, nodata: 3 };
export const SHORT_OUTCOME = { pass: 'Pass', review: 'Review', outside: 'Outside', nodata: 'Insufficient data' };

function critValue(view, key) {
  const c = (view?.result?.criteria || []).find((x) => x.key === key);
  const n = Number(c?.value);
  return c && c.value != null && Number.isFinite(n) ? n : null;
}

/** Rank deals for the landing dashboard: outcome, then PDP PV10 desc, then label. */
export function rankDeals(deals) {
  const rank = (d) => {
    const o = d?.result?.outcome;
    return o in OUTCOME_RANK ? OUTCOME_RANK[o] : 4;
  };
  return [...(deals || [])].sort((a, b) => {
    const r = rank(a) - rank(b);
    if (r) return r;
    const pa = critValue(a, 'pdpPv10');
    const pb = critValue(b, 'pdpPv10');
    if (pa != null || pb != null) {
      if (pa == null) return 1;
      if (pb == null) return -1;
      if (pb !== pa) return pb - pa;
    }
    return String(a?.label || '').localeCompare(String(b?.label || ''));
  });
}

/** One-line top reason for a deal's outcome (first outside, then review, then missing data). */
export function topReason(result) {
  if (!result) return '';
  const crit = result.criteria || [];
  const fmt = (c) =>
    `${c.label}: ${fmtUnit(c.value, c.unit)}${c.detail ? ` (${c.detail})` : ''}`;
  const outside = crit.find((c) => c.status === 'outside');
  if (outside) return fmt(outside);
  const review = crit.find((c) => c.status === 'review');
  if (review) return fmt(review);
  const nodata = crit.find((c) => c.status === 'nodata' && !c.optional);
  if (nodata) return `${nodata.label}: no data${nodata.detail ? ` (${nodata.detail})` : ''}`;
  return result.risks?.[0]?.title || result.reason || '';
}
